import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import debounce from 'lodash.debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import { FaFlag } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import CourseAnalyticsModal from './Course/DetailsModal/CourseAnalyticsModal';
import MultiSelect from '../../ClassSearch/MultiSelect';

const CoursesAnalytics = () => {
  // Outer component hooks for routing
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const semesterFromLink = searchParams.get('semester');

  const { fetchedCourseData, fetchedMajorRecs } = useAuth();
  const { currentUser } = useAuth();
  const sentinelRef = useRef(null);

  // Local state for data, filters, etc.
  const [displayedData, setDisplayedData] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(1);
  const [displayedCourseCount, setDisplayedCourseCount] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Multi-select filter states
  const [levelFilter, setLevelFilter] = useState([]);
  const [majorFilter, setMajorFilter] = useState([]);
  const [creditsFilter, setCreditsFilter] = useState([]);

  const [userRecsData, setUserRecsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [surveyAnswers, setSurveyAnswers] = useState([]);
  const [finalizedCourseIds, setFinalizedCourseIds] = useState([]);
  const [showRecommendedCourses, setShowRecommendedCourses] = useState(false);
  const [transformedMajorRecData, setTransformedMajorRecData] = useState([]);

  // Flag sort filter state (to hide courses where planned equals projected)
  const [flagSort, setFlagSort] = useState(false);

  // Sort state (key and direction)
  const [sortConfig, setSortConfig] = useState({
    key: 'courseInfo_courseNumber',
    direction: 'asc'
  });

  // Debounce delay (in ms)
  const DEBOUNCE_DELAY = 1;

  // Transform fetched major recs (if any)
  useEffect(() => {
    if (fetchedMajorRecs) {
      const transformedData = fetchedMajorRecs.reduce((acc, curr) => {
        const { Major, ...categories } = curr;
        acc[Major] = categories;
        return acc;
      }, {});
      setTransformedMajorRecData(transformedData);
    }
  }, [fetchedMajorRecs]);

  // Fetch user data from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = currentUser;
        if (user) {
          const userDocRef = db.collection('users').doc(user.uid);
          const userDoc = await userDocRef.get();
          if (userDoc.exists) {
            const userData = userDoc.data();
            const activeTab = userData.activeTab || '';
            const courseIds = userData.schedules[activeTab] || [];
            const backendSurveyAnswers = userData.surveyAnswers || [];
            const backendUserRecData = userData.recommendationRatings || {};
            setFinalizedCourseIds(courseIds);
            setSurveyAnswers(backendSurveyAnswers);
            setUserRecsData(backendUserRecData);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
      setLoading(false);
    };
    fetchUserData();
  }, [currentUser]);

  // Build unique filter options using useMemo
  const uniqueLevels = useMemo(() => {
    if (!fetchedCourseData) return [];
    const levels = new Set();
    Object.values(fetchedCourseData).forEach((item) => {
      if (item.courseYear) levels.add(item.courseYear);
    });
    return Array.from(levels).sort((a, b) => parseInt(a) - parseInt(b));
  }, [fetchedCourseData]);

  const uniqueMajors = useMemo(() => {
    if (!fetchedCourseData) return [];
    const majors = new Set();
    Object.values(fetchedCourseData).forEach((item) => {
      if (item.Major) {
        item.Major.split(',').forEach((major) => majors.add(major.trim()));
      }
    });
    return Array.from(majors).sort();
  }, [fetchedCourseData]);

  const uniqueCredits = useMemo(() => {
    if (!fetchedCourseData) return [];
    const credits = new Set();
    Object.values(fetchedCourseData).forEach((item) => {
      if (item.Credits) credits.add(item.Credits);
    });
    return Array.from(credits).sort((a, b) => parseFloat(a) - parseFloat(b));
  }, [fetchedCourseData]);

  const levelSuggestions = useMemo(
    () => uniqueLevels.map((level) => `${level}`),
    [uniqueLevels]
  );
  const majorSuggestions = useMemo(() => uniqueMajors, [uniqueMajors]);
  const creditsSuggestions = useMemo(
    () => uniqueCredits.map((credit) => `${credit} Credit(s)`),
    [uniqueCredits]
  );

  // Handle sort header clicks by toggling sort direction or changing sort key
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Debounced filter and sort
  const debouncedFilter = useCallback(
    debounce(() => {
      filterDataAndSetDisplay();
    }, DEBOUNCE_DELAY),
    [
      fetchedCourseData,
      searchQuery,
      levelFilter,
      majorFilter,
      creditsFilter,
      showRecommendedCourses,
      surveyAnswers,
      userRecsData,
      transformedMajorRecData,
      flagSort,
      sortConfig
    ]
  );

  useEffect(() => {
    debouncedFilter();
    return debouncedFilter.cancel;
  }, [
    fetchedCourseData,
    searchQuery,
    levelFilter,
    majorFilter,
    creditsFilter,
    showRecommendedCourses,
    surveyAnswers,
    userRecsData,
    transformedMajorRecData,
    flagSort,
    sortConfig,
    debouncedFilter
  ]);

  const filterDataAndSetDisplay = () => {
    if (fetchedCourseData) {
      const filtered = Object.values(fetchedCourseData).filter((item) => {
        const majors = item.Major.split(',').map((major) => major.trim());
        if (
          levelFilter.length > 0 &&
          !levelFilter.some((filter) => parseInt(filter) === parseInt(item.courseYear))
        ) {
          return false;
        }
        if (
          majorFilter.length > 0 &&
          !majorFilter.some((filter) => majors.includes(filter))
        ) {
          return false;
        }
        if (
          creditsFilter.length > 0 &&
          !creditsFilter.some(
            (filter) =>
              parseFloat(filter.replace(' Credit(s)', '')) === parseFloat(item.Credits)
          )
        ) {
          return false;
        }
        if (flagSort && item.OfferedSections === item.ProjectedSections) {
          return false;
        }
        const nameMatch = item.courseInfo_courseName
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const numberMatch = item.courseInfo_courseNumber
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        return nameMatch || numberMatch;
      });

      const sortedData = filtered.sort((a, b) => {
        let aVal, bVal;
        if (sortConfig.key === 'fillRate') {
          aVal = Math.min(Math.round((a.ProjectedSeats / a.OfferedSeats) * 100), 100);
          bVal = Math.min(Math.round((b.ProjectedSeats / b.OfferedSeats) * 100), 100);
        } else {
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
          if (typeof aVal === 'string') aVal = aVal.toLowerCase();
          if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });

      setFilteredData(sortedData);
      setRecommendedCourses(sortedData);
      setDisplayedData(sortedData.slice(0, 100));
      setCurrentBatch(1);
      setDisplayedCourseCount(sortedData.length);
    }
  };

  // Infinite scroll: load more data when sentinel comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayedData.length < filteredData.length) {
          loadMoreData();
        }
      },
      {
        threshold: 0,
        root: sentinelRef.current ? sentinelRef.current.parentElement : null,
        rootMargin: '100px'
      }
    );
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [displayedData, filteredData]);

  const loadMoreData = () => {
    const nextBatch = currentBatch + 1;
    const newData = filteredData.slice(0, nextBatch * 100);
    setDisplayedData(newData);
    setCurrentBatch(nextBatch);
  };

  // Reset filters
  const handleResetFilters = () => {
    setLevelFilter([]);
    setMajorFilter([]);
    setCreditsFilter([]);
    setShowRecommendedCourses(false);
    setSearchQuery('');
    setFlagSort(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div role="status">
          <svg
            aria-hidden="true"
            className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading</span>
        </div>
      </div>
    );
  }

  // --- Internal component for each course row ---
  // Now receiving navigate and location via props from the outer component.
  const CourseRow = ({ index, classData, fillRate, navigate, location }) => {
    const [isWarningModalVisible, setIsWarningModalVisible] = useState(false);
    const warningModalRef = useRef(null);
    const flagRef = useRef(null);

    // Instead of opening a modal via state, navigate to the new URL
    const handleDetailsButtonClick = (e) => {
      e.stopPropagation();
      navigate(`${location.pathname}/${classData.courseInfo_courseNumber}`);
    };

    const handleFlagClick = (e) => {
      e.stopPropagation();
      setIsWarningModalVisible((prev) => !prev);
    };

    const handleOutsideClick = (event) => {
      if (warningModalRef.current && !warningModalRef.current.contains(event.target)) {
        setIsWarningModalVisible(false);
      }
    };

    useEffect(() => {
      if (isWarningModalVisible) {
        document.addEventListener('click', handleOutsideClick);
      } else {
        document.removeEventListener('click', handleOutsideClick);
      }
      return () => {
        document.removeEventListener('click', handleOutsideClick);
      };
    }, [isWarningModalVisible]);

    let flag = null;
    let warningMessage = '';
    if (classData.ProjectedSections < classData.OfferedSections) {
      flag = (
        <span
          ref={flagRef}
          title="Too Many Resources Allocated"
          className="text-blue-500 hover:text-blue-700 cursor-pointer"
          onClick={handleFlagClick}
        >
          <FaFlag />
        </span>
      );
      warningMessage = `Warning: ${classData.courseInfo_courseNumber} is projected to have more sections allocated than needed.`;
    } else if (classData.ProjectedSections > classData.OfferedSections) {
      flag = (
        <span
          ref={flagRef}
          title="Additional Resources Needed"
          className="text-red-500 hover:text-red-700 cursor-pointer"
          onClick={handleFlagClick}
        >
          <FaFlag />
        </span>
      );
      warningMessage = `Warning: ${classData.courseInfo_courseNumber} is projected to require more sections than currently allocated.`;
    }

    const fillRateBarClass =
      fillRate < 50
        ? 'bg-red-600 text-red-100'
        : fillRate < 85
        ? 'bg-yellow-400 text-yellow-100'
        : fillRate < 95
        ? 'bg-green-600 text-green-100'
        : 'bg-blue-600 text-blue-100';

    return (
      <tr className="bg-white border-b hover:bg-gray-50">
        <td
          className="pl-4 pr-2 pt-3 pb-1 font-bold whitespace-nowrap"
          style={{ width: '15%' }}
        >
          {classData.courseInfo_courseNumber}
        </td>
        <td className="px-2 pt-3 pb-1 break-words" style={{ width: '35%' }}>
          {classData.courseInfo_courseName}
        </td>
        <td
          className="px-2 pt-2 pb-1 whitespace-nowrap"
          style={{ width: '15%' }}
          onClick={() => handleSort('OfferedSections')}
        >
          <div className="font-semibold text-gray-700">
            {classData.OfferedSections} sections
          </div>
          <div className="text-xs text-gray-500">{classData.OfferedSeats} seats</div>
        </td>
        <td
          className="px-2 pt-2 pb-2 whitespace-nowrap"
          style={{ width: '15%' }}
          onClick={() => handleSort('ProjectedSections')}
        >
          <div className="font-semibold text-gray-700">
            {classData.ProjectedSections} sections
          </div>
          <div className="text-xs text-gray-500">{classData.ProjectedSeats} seats</div>
        </td>
        <td className="px-2 pt-3 whitespace-nowrap" style={{ width: '15%' }}>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`text-xs font-medium text-center leading-none rounded-full mt-0.5 pt-0.5 ${fillRateBarClass}`}
              style={{ width: `${fillRate}%`, height: '100%' }}
            >
              {fillRate}%
            </div>
          </div>
        </td>
        <td className="px-2 pt-3 pb-1 whitespace-nowrap" style={{ width: '16%' }}>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDetailsButtonClick}
              className="text-blue-700 hover:text-white border border-blue-700 hover:bg-blue-800 focus:outline-none font-medium rounded text-xs px-2 py-1 transition-all duration-300"
            >
              Details
            </button>
            {flag && (
              <div className="relative inline-block">
                {flag}
                {isWarningModalVisible && (
                  <div
                    ref={warningModalRef}
                    className="absolute z-10 bg-gray-100 shadow-lg border rounded p-2 w-80 bottom-full right-0 mb-2"
                  >
                    <div className="flex justify-between items-start">
                      <div className="text-sm text-gray-700 break-words whitespace-normal w-full">
                        {warningMessage}
                      </div>
                      <button
                        onClick={() => setIsWarningModalVisible(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <IoClose size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="flex">
      {/* Sidebar for filters */}
      <div className="w-[25vw]">
        <div className="p-4 bg-gray-50 shadow-md border rounded-lg">
          <h5 className="text-lg text-center font-bold mb-4">Filters</h5>
          {/* Search */}
          <div className="mb-2">
            <label
              htmlFor="searchQuery"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Search:
            </label>
            <input
              type="text"
              id="searchQuery"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          {/* Level Filter */}
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Level:
            </label>
            <MultiSelect
              suggestions={levelSuggestions}
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              placeholder="Select Level(s)"
              required={false}
              unselectable={[]}
              tagColor="blue"
            />
          </div>
          {/* Major Filter */}
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Major:
            </label>
            <MultiSelect
              suggestions={majorSuggestions}
              value={majorFilter}
              onChange={(e) => setMajorFilter(e.target.value)}
              placeholder="Select Major(s)"
              required={false}
              unselectable={[]}
              tagColor="green"
            />
          </div>
          {/* Credits Filter */}
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credits:
            </label>
            <MultiSelect
              suggestions={creditsSuggestions}
              value={creditsFilter}
              onChange={(e) => setCreditsFilter(e.target.value)}
              placeholder="Select Credit(s)"
              required={false}
              unselectable={[]}
              tagColor="purple"
            />
          </div>
          {/* Flag Sort Checkbox */}
          <div className="mb-3 flex items-center">
            <input
              type="checkbox"
              id="flagSort"
              checked={flagSort}
              onChange={(e) => setFlagSort(e.target.checked)}
              className="mr-1.5 text-blue-600 bg-gray-100 border-gray-300 rounded-sm"
            />
            <label htmlFor="flagSort" className="block text-sm font-medium text-gray-700">
              Show Flagged Courses
            </label>
          </div>
          <button
            className="w-full bg-blue-500 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded mt-1"
            onClick={handleResetFilters}
          >
            Reset Filters
          </button>
          <div className="flex justify-center mt-3">
            <span className="bg-gray-200 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {displayedCourseCount} Courses Displayed
            </span>
          </div>
        </div>
      </div>
      {/* Courses Table container */}
      <div className="w-full ml-3 w-2/3">
        <div className="p-4 bg-gray-50 shadow-md border rounded-lg h-[90vh]">
          <div className="flex items-center justify-center mb-4 relative">
            <h5 className="text-lg font-bold text-center">Courses</h5>
          </div>
          {/* The table wrapper now scrolls vertically while keeping the header fixed */}
          <div className="relative overflow-x-auto h-[80vh] overflow-y-auto">
            <table className="table-fixed w-full text-sm text-left text-gray-500" id="sorting-table">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th
                    scope="col"
                    className="pl-4 pr-2 pt-3 pb-1 cursor-pointer whitespace-nowrap"
                    style={{ width: '15%' }}
                    onClick={() => handleSort('courseInfo_courseNumber')}
                  >
                    Course ID{' '}
                    <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: '10px' }} />
                  </th>
                  <th
                    scope="col"
                    className="px-2 pt-3 pb-1 cursor-pointer whitespace-nowrap"
                    style={{ width: '35%' }}
                    onClick={() => handleSort('courseInfo_courseName')}
                  >
                    Course Name{' '}
                    <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: '10px' }} />
                  </th>
                  <th
                    scope="col"
                    className="px-2 pt-2 pb-2 cursor-pointer whitespace-nowrap"
                    style={{ width: '15%' }}
                    onClick={() => handleSort('OfferedSections')}
                  >
                    <div className="flex flex-col">
                      <span>Offered</span>
                      <span className="text-xs">(Planned)</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 pt-2 pb-2 cursor-pointer whitespace-nowrap"
                    style={{ width: '15%' }}
                    onClick={() => handleSort('ProjectedSections')}
                  >
                    <div className="flex flex-col">
                      <span>Needed</span>
                      <span className="text-xs">(Projected)</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-2 pt-3 pb-1 cursor-pointer whitespace-nowrap"
                    style={{ width: '15%' }}
                    onClick={() => handleSort('fillRate')}
                  >
                    Fill Rate{' '}
                    <FontAwesomeIcon icon={faSort} className="ml-1" style={{ fontSize: '10px' }} />
                  </th>
                  <th
                    scope="col"
                    className="px-2 pt-3 pb-1 whitespace-nowrap"
                    style={{ width: '11%' }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedData.map((classData, index) => {
                  const fillRateValue = Math.min(
                    Math.round((classData.ProjectedSeats / classData.OfferedSeats) * 100),
                    100
                  );
                  return (
                    <CourseRow
                      key={index}
                      index={index}
                      classData={classData}
                      fillRate={fillRateValue}
                      navigate={navigate}
                      location={location}
                    />
                  );
                })}
              </tbody>
            </table>
            <div ref={sentinelRef}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursesAnalytics;
